import axios, { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import type {
  InsufficientFundsDetail,
  FreezeCapReachedDetail,
  Wallet,
  WalletPurchaseResponse,
  WalletPurchaseVariables,
} from 'src/types/wallet.types';
import { queryKeys } from 'src/queries/keys';
import { useUserRole } from 'src/hooks/useUserRole';

// Parents get a deliberate 403 (child-only rows), so the query is gated rather than left to fail on
// every mount.
export const useWallet = () => {
  const { isChild } = useUserRole();

  return useQuery<Wallet>({
    queryFn: async () => (await axios.get<Wallet>('/api/wallet')).data,
    queryKey: queryKeys.wallet(),
    enabled: isChild,
    staleTime: 30_000,
  });
};

// NEVER coerce to 0: `undefined` means the wallet has not resolved yet and consumers guard on it.
export const useBabzBalance = (): number | undefined => useWallet().data?.babz;

// A key for one purchase *intent*, not one request.
export const newPurchaseIdempotencyKey = (): string => uuidv4();

export const asInsufficientFunds = (
  error: unknown,
): InsufficientFundsDetail | null => {
  const detail = (error as AxiosError<{ message?: unknown }> | undefined)
    ?.response?.data?.message;

  return typeof detail === 'object' &&
    detail !== null &&
    (detail as InsufficientFundsDetail).code === 'INSUFFICIENT_FUNDS'
    ? (detail as InsufficientFundsDetail)
    : null;
};

export const asFreezeCapReached = (
  error: unknown,
): FreezeCapReachedDetail | null => {
  const detail = (error as AxiosError<{ message?: unknown }> | undefined)
    ?.response?.data?.message;

  return typeof detail === 'object' &&
    detail !== null &&
    (detail as FreezeCapReachedDetail).code === 'FREEZE_CAP_REACHED'
    ? (detail as FreezeCapReachedDetail)
    : null;
};

export const useWalletPurchase = () => {
  const queryClient = useQueryClient();

  return useMutation<
    WalletPurchaseResponse,
    AxiosError<{ message: InsufficientFundsDetail | FreezeCapReachedDetail | string }>,
    WalletPurchaseVariables
  >({
    mutationFn: async ({ purchase_source: _source, ...body }) =>
      (await axios.post<WalletPurchaseResponse>('/api/wallet/purchase', body))
        .data,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Wallet>(queryKeys.wallet(), data.wallet);
      if (variables.item === 'streak_freeze') {
        void logAnalyticsEvent('streak_freeze_purchased', {
          quantity: data.quantity,
          price: data.unit_price,
          held_after: data.wallet.streak_freezes,
          purchase_source: variables.purchase_source,
        });
      }
    },
  });
};
